/* eslint-disable eag/match-named-export -- Pre-existing grab-bag file with several exports */
import {ThemeColorPath, ThemeColorTextPath} from 'platform/foundation';

export interface SalesLeaguePodiumMedal {
  labelKey: string;
  /**
   * Supplied by design, then darkened on request — the whole ramp by ~12%, and the start
   * of it again by the same, so each medal deepens toward its top-left corner. CSS
   * gradients rather than theme tokens: the palette carries no metallic ramps.
   */
  background: string;
  textColor: ThemeColorTextPath;
  /** Divider above the stat strip, tuned to the card's own background. */
  dividerColor: ThemeColorPath;
}

/** Indexed by place, so entry 0 is gold. */
export const SALES_LEAGUE_PODIUM_MEDALS: readonly SalesLeaguePodiumMedal[] = [
  {
    labelKey: 'page.salesLeague.podium.gold',
    background: 'linear-gradient(135deg, rgb(151, 114, 16), rgb(211, 175, 65))',
    textColor: 'white',
    dividerColor: 'palettes.white.10.20',
  },
  {
    labelKey: 'page.salesLeague.podium.silver',
    background: 'linear-gradient(135deg, rgb(98, 106, 121), rgb(155, 164, 176))',
    textColor: 'white',
    dividerColor: 'palettes.white.10.20',
  },
  {
    labelKey: 'page.salesLeague.podium.bronze',
    background: 'linear-gradient(135deg, rgb(141, 82, 34), rgb(195, 134, 78))',
    textColor: 'white',
    dividerColor: 'palettes.white.10.20',
  },
];

export const SALES_LEAGUE_PODIUM_PLACES = SALES_LEAGUE_PODIUM_MEDALS.length;

export const SALES_LEAGUE_PODIUM_WINNER_PLACE_INDEX = 0;

/** Silver, gold, bronze — the winner stands in the middle, raised, as on a real podium. */
export const SALES_LEAGUE_PODIUM_ROW_ORDER = [1, 0, 2];

/** Stacked on narrow viewports, where the podium metaphor cannot survive anyway. */
export const SALES_LEAGUE_PODIUM_COLUMN_ORDER = [0, 1, 2];

/*
  The card's geometry, shared with the skeleton that stands in for it. Line values are the
  line-heights of the text they hold — Heading 3 is 28px (7), Heading 4 24px (6), Text small
  20px (5), Text xSmall 16px (4) — so a skeleton built from them is exactly as tall as the
  card it replaces and the layout does not jump when the board arrives.
*/
export const SALES_LEAGUE_PODIUM_CARD_PADDING_HORIZONTAL = 4;
export const SALES_LEAGUE_PODIUM_WINNER_PADDING_VERTICAL = 5;
export const SALES_LEAGUE_PODIUM_RUNNER_UP_PADDING_VERTICAL = 4;
export const SALES_LEAGUE_PODIUM_SECTION_SPACING = 4;
export const SALES_LEAGUE_PODIUM_IDENTITY_SPACING = 3;
export const SALES_LEAGUE_PODIUM_NAME_SPACING = 1;
export const SALES_LEAGUE_PODIUM_STAT_SPACING = 4;
export const SALES_LEAGUE_PODIUM_WINNER_NAME_LINE = 7;
export const SALES_LEAGUE_PODIUM_RUNNER_UP_NAME_LINE = 6;
export const SALES_LEAGUE_PODIUM_SUBTITLE_LINE = 5;
export const SALES_LEAGUE_PODIUM_STAT_LABEL_LINE = 4;
export const SALES_LEAGUE_PODIUM_STAT_COUNT = 3;

export const SALES_LEAGUE_PODIUM_WINNER_FLEX = 1.35;
export const SALES_LEAGUE_PODIUM_RUNNER_UP_FLEX = 1;

/** Rounded tile carrying the place number, translucent so the gradient reads through. */
export const SALES_LEAGUE_PODIUM_PLACE_BADGE_SIZE = 6;
export const SALES_LEAGUE_PODIUM_PLACE_BADGE_COLOR: ThemeColorPath = 'palettes.white.10.20';
export const SALES_LEAGUE_PODIUM_UNFILLED_PLACE_BADGE_COLOR: ThemeColorPath =
  'palettes.neutral.30.100';

/**
 * The leading visual matches the text block beside it: the name's line-height, the gap
 * under it, and the subtitle's line-height. Gold's name is a size larger, so its circle is
 * too.
 */
export const SALES_LEAGUE_PODIUM_WINNER_VISUAL_SIZE = 13;
export const SALES_LEAGUE_PODIUM_RUNNER_UP_VISUAL_SIZE = 12;

export const SALES_LEAGUE_PODIUM_UNFILLED_BORDER_COLOR: ThemeColorPath = 'palettes.neutral.40.100';
export const SALES_LEAGUE_PODIUM_UNFILLED_TEXT_COLOR: ThemeColorTextPath = 'tertiary';
